'use client';
import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const LAST_UPDATED = 'March 26, 2026';
const COMPANY = 'DA1MAMBA Professional Detailing';
const EMAIL = 'info@da1mambadetailing.com';
const PHONE_DISPLAY = '(949) 293-5721';
const CALL_LINK = 'tel:+19492935721';

const sections = [
  {
    title: 'Acceptance of Terms',
    content: [
      `By booking a service, using our website, or engaging with ${COMPANY}, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.`,
    ],
  },
  {
    title: 'Services',
    content: [
      'DA1MAMBA Professional Detailing provides mobile automotive detailing services including but not limited to: exterior washing, interior cleaning, paint correction, ceramic coating, and detailing packages.',
      'All services are performed at the location agreed upon at the time of booking. We reserve the right to refuse service at our discretion.',
    ],
  },
  {
    title: 'Appointments and Cancellations',
    content: [
      'We ask that you provide at least 24 hours notice if you need to cancel or reschedule your appointment. Late cancellations or no-shows may be subject to a cancellation fee.',
      'We reserve the right to reschedule appointments due to weather conditions, equipment issues, or other circumstances beyond our control. We will notify you as soon as possible in such cases.',
    ],
  },
  {
    title: 'Pricing and Payment',
    content: [
      'Prices are subject to change without notice. Final pricing may vary based on vehicle size, condition, and specific service requirements assessed at the time of service.',
      'Payment is due upon completion of services unless otherwise agreed in writing. We accept major credit cards, cash, and other payment methods as indicated at the time of booking.',
    ],
  },
  {
    title: 'Vehicle Condition and Pre-Existing Damage',
    content: [
      'We take great care with every vehicle we service. However, we are not responsible for pre-existing damage, defects, or conditions present before service begins.',
      'We recommend you document any existing scratches, dents, or damage before your appointment. We may also photograph your vehicle prior to service for our records.',
      'Certain vehicle conditions (e.g., heavily oxidized paint, deep scratches, or structural damage) may limit the results achievable through detailing services.',
    ],
  },
  {
    title: 'Liability',
    content: [
      `${COMPANY}'s liability for any claim arising from our services is limited to the amount paid for the specific service in question.`,
      'We are not liable for indirect, incidental, or consequential damages. We are not responsible for items left in the vehicle during service.',
    ],
  },
  {
    title: 'Ceramic Coating and Paint Correction',
    content: [
      'Results from paint correction and ceramic coating services depend on the current condition of your vehicle\'s paint. We will provide a realistic assessment before beginning work.',
      'Ceramic coating warranties, if offered, are subject to proper maintenance and care as outlined in our aftercare instructions. Failure to follow aftercare guidelines may void any warranty.',
    ],
  },
  {
    title: 'Intellectual Property',
    content: [
      'All content on our website, including text, images, logos, and graphics, is the property of DA1MAMBA Professional Detailing and may not be reproduced without written permission.',
    ],
  },
  {
    title: 'Governing Law',
    content: [
      'These Terms of Service are governed by the laws of the State of California. Any disputes arising from these terms or our services shall be resolved in the courts of Orange County, California.',
    ],
  },
  {
    title: 'Changes to Terms',
    content: [
      'We reserve the right to update these Terms of Service at any time. Changes will be posted on this page with an updated effective date. Continued use of our services after changes constitutes acceptance of the updated terms.',
    ],
  },
  {
    title: 'Contact Us',
    content: [
      `If you have any questions about these Terms of Service, please contact us at ${EMAIL} or call us at ${PHONE_DISPLAY}.`,
    ],
  },
];

export default function TermsOfServicePage() {
  return (
    <div style={{ background: 'var(--mamba-black)', minHeight: '100vh' }}>
      <Header />
      <main>
        {/* Hero */}
        <section
          style={{
            paddingTop: '140px',
            paddingBottom: '80px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 60% 60% at 80% 40%, rgba(107,33,168,0.1) 0%, transparent 70%)' }}
          />
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <div className="label-gold mb-5">
              <span style={{ width: '40px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
              Legal
            </div>
            <h1
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(36px, 5vw, 64px)',
                lineHeight: '1.0',
                letterSpacing: '-0.03em',
                color: 'var(--white)',
                marginBottom: '20px',
              }}
            >
              Terms of{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, var(--mamba-purple-bright), #9333ea 50%, var(--mamba-gold-light))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Service
              </span>
            </h1>
            <p style={{ color: 'var(--gray-dim)', fontSize: '14px', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.04em' }}>
              Last updated: {LAST_UPDATED}
            </p>
          </div>
        </section>

        {/* Content */}
        <section style={{ paddingBottom: '120px' }}>
          <div className="max-w-4xl mx-auto px-6">
            {/* Intro */}
            <div
              style={{
                background: 'var(--mamba-graphite)',
                border: '1px solid rgba(107,33,168,0.2)',
                borderRadius: '2px',
                padding: '32px',
                marginBottom: '48px',
              }}
            >
              <p style={{ color: 'var(--cool-gray)', fontSize: '16px', lineHeight: '1.8', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
                Please read these Terms of Service carefully before using our website or booking any services with {COMPANY}. These terms govern your use of our services and website.
              </p>
            </div>

            {/* Sections */}
            <div className="flex flex-col gap-10">
              {sections?.map((section, i) => (
                <div
                  key={i}
                  style={{
                    borderBottom: '1px solid rgba(107,33,168,0.12)',
                    paddingBottom: '40px',
                  }}
                >
                  <h2
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 700,
                      fontSize: '20px',
                      color: 'var(--white)',
                      marginBottom: '16px',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {i + 1}. {section?.title}
                  </h2>
                  <div className="flex flex-col gap-4">
                    {section?.content?.map((para, j) => (
                      <p
                        key={j}
                        style={{ color: 'var(--cool-gray)', fontSize: '15px', lineHeight: '1.8', fontFamily: 'DM Sans, sans-serif', margin: 0 }}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div
              style={{
                marginTop: '60px',
                background: 'var(--mamba-graphite)',
                border: '1px solid rgba(107,33,168,0.25)',
                borderRadius: '2px',
                padding: '40px',
                textAlign: 'center',
              }}
            >
              <div className="label-gold mb-4" style={{ justifyContent: 'center' }}>Questions?</div>
              <p style={{ color: 'var(--cool-gray)', fontSize: '15px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif', marginBottom: '24px' }}>
                Have questions about our terms? We're happy to clarify anything before you book.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`mailto:${EMAIL}`}
                  className="btn-primary"
                  style={{ fontSize: '13px', padding: '14px 28px' }}
                >
                  Email Us
                </a>
                <a
                  href={CALL_LINK}
                  className="btn-gold"
                  style={{ fontSize: '13px', padding: '13px 24px' }}
                >
                  Call {PHONE_DISPLAY}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
